import { FunctionComponent, useState, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { Delete as DeleteIcon } from '../../../components/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducer';
import { ToolData } from '../../../types/tool';
import { Typography, IconButton, Snackbar, Dialog, Select, Table, TableRow, TableCell } from './../../../tailwindcomponents';
import toolsApi from './../../../api/tools';
import taskApi from './../../../api/tasks';


type ToolRegisterListProps = {
  tabValue: number,
  onChangeTabValue: Function
}

//When CDATA is missing
const exampleTails: string[] = [
  'TF-FIA',
  'TF-FIC',
  'TF-FIG',
  'TF-FIH',
  'TE-FIK',
  'TE-FIN',
  'TF-FIO',
  'TF-FIP',
  'TF-FIU',
  'TF-FIV',
  'TF-FIX',
  'TF-FXG',
  'TE-EXH',
  'TE-ICA',
  'TE-ICB',
  'TE-ICC',
  'TE-ICD',
  'TE-ICE',
  'TE-ICG',
  'TE-ICI',
  'TE-ICL',
  'TE-ICM',
  'TE-ICN',
  'TE-ICO',
  'TE-ICP',
  'TE-ICR',
  'TF-ICS',
  'TF-ICU',
  'TF-ICY',
  'TE-ISH',
  'TF-ISJ',
  'TE-ISN',
  'TF-ISO',
  'TE_ISR',
  'TF-ISV',
  'TE_ISW',
  'TF-ISX',
  'TE-LLL',
]

const ToolRegisterList: FunctionComponent<ToolRegisterListProps> = ({ tabValue, onChangeTabValue }) => {
  const [scanInput, setScanInput] = useState<string>("");
  const [selected_tools, setSelectedTools] = useState(Array<ToolData>());
  const [extended_tools, setExtendedTools] = useState(Array<ToolData>());
  const [snackbar_message, setSnackbarMessage] = useState<string>("");
  const [extended_tools_dialog_visible, setExtendedToolsDialog] = useState<boolean>(false);
  const [showAreYouSureDialog, setShowAreYouSureDialog] = useState<boolean>(false);
  const [employeeUserId, setEmployeeUserId] = useState<number>(0);
  const store = useSelector((state: RootState) => state);
  const [tails, setTails] = useState<string[]>(exampleTails);
  const [selectedTail, setSelectedTail] = useState<string>("");
  const [selectTailDisabled, setSelectTailDisabled] = useState<boolean>(true);

  // per-item removal timers (cascade)
  const removalTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const nextRemovalAtRef = useRef<number>(0);
  const toolKey = (t: ToolData) => String(t.mxi_inv_no_id ?? t.rf_id);

  const scheduleRemoval = (tool: ToolData, minVisibleMs = 3000, staggerMs = 300) => {
    const key = toolKey(tool);

    // Clear any previous timer for this tool (just in case)
    const existing = removalTimersRef.current.get(key);
    if (existing) clearTimeout(existing);

    const now = Date.now();
    const earliestAllowed = now + minVisibleMs;

    // Ensure tools are removed in the order they were scheduled:
    // use a "next open slot" so removals cascade one after another.
    const scheduledAt = Math.max(earliestAllowed, nextRemovalAtRef.current || earliestAllowed);
    const delay = Math.max(0, scheduledAt - now);

    // Reserve the following slot for the next tool
    nextRemovalAtRef.current = scheduledAt + staggerMs;

    const t = setTimeout(() => {
      setSelectedTools(prev => {
        // Remove THIS specific tool (matches the original “first-in goes first”
        // semantics when scanning in order and scheduling right away)
        const next = prev.filter(x => toolKey(x) !== key);
        if (next.length === 0) {
          setSelectTailDisabled(true);
          nextRemovalAtRef.current = 0; // reset cascade when empty
        }
        return next;
      });
      removalTimersRef.current.delete(key);
    }, delay);

    removalTimersRef.current.set(key, t);
  };

  useEffect(() => {
    const escFunction = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        setSelectedTools([]);
        setScanInput("");
      }
      // F9 toggles click event for Tools Out
      if (event.keyCode === 120) { onChangeTabValue(0) }
      // F10 toggles click event for Return Tool Box
      if (event.keyCode === 121) { event.preventDefault(); onChangeTabValue(1) }
      if (event.keyCode === 13) { document.getElementById("press-Enter")?.click() }
    }
    document.addEventListener('keydown', escFunction, false);
    setSelectedTools([]);
    taskApi.GetAllTailNumbers().then(res => {
      setTails(res.data);
    })
  }, [onChangeTabValue]);

  const handlerToLoanTools = (extending_tools: boolean, givenEmployeeUserId: number | null, tail: string | null) => {
    if(tabValue === 0){
      toolsApi.loanToolsOut(selected_tools, givenEmployeeUserId, tail, extending_tools, "verkf@icelandair.is").then(res => {
        setSnackbarMessage(res.message);
        if (res.status === false) {
          if (res.borrowed_tools) {
            //user is not allowed to take these tools
            setExtendedTools(res.borrowed_tools);
            setExtendedToolsDialog(true);
          } else {
            //If the plane is not in hangar, reenable the select tail function
            setSelectTailDisabled(false);
          }
        } else {
          setSelectedTools([]);
        }
      });
    }
  }

  const handleEnterPressed = () => {
    const tempScanInput = scanInput.trim();
    if (tempScanInput.length !== 24 && tempScanInput.length > 0) {
      //User
      const user = store.employees.all_employees.filter(s => s.user_card_code === tempScanInput);
      if (user.length === 0) { alert("This user is not a registered user") }
      if (user.length > 0 && selected_tools.length > 0) {
        setEmployeeUserId(user[0].Id);
        handlerToLoanTools(false, user[0].Id, null);
      }
      setScanInput("");
    }
    if (tempScanInput.length === 24) {
      const tool = store.tools.mxi_tools.filter(s => s.rf_id === tempScanInput);
      if (tool.length > 0) {
        const selectedTool = selected_tools.filter(s => s.rf_id === tool[0].rf_id);
        if (selectedTool.length === 0) {
          // (keep your original mutation pattern)
          selected_tools.push(tool[0]);
          setSelectedTools(selected_tools);
          setSelectTailDisabled(false);
        }
        if (tabValue === 1) {
          //Return tool
          toolsApi.loanToolIn(tool[0].mxi_inv_no_id, "verkf@icelandair.is").then(res => {
            if (res.status === false) {
              alert(res.message);
            } else { //Tool successfully returned
              setSnackbarMessage(res.message);

              // --- CHANGED: use per-item scheduled removal (min 5s, then cascade) ---
              scheduleRemoval(tool[0], 5000, 300);
            }
          });
        }
        setScanInput("");
      }
      else {
        alert("No Tool whith this RFID");
        setScanInput("");
      }
    }
  }

  const handleModifeRFID = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toString().toUpperCase();
    if (value.length >= 25) {
      setScanInput("");
    } else {
      setScanInput(value);
    }
  }

  setInterval(function () {
    if (window.location.href.includes("/tool-register")) {
      try {
        const returnInput = document.getElementById('text-Box-Loan');
        returnInput?.focus();
      } catch { }
    }
  }, 1000);

  const handleDeleteIconClick = (row: ToolData) => {
    // --- NEW: clear any pending timer for this specific tool ---
    const key = toolKey(row);
    const timer = removalTimersRef.current.get(key);
    if (timer) {
      clearTimeout(timer);
      removalTimersRef.current.delete(key);
    }

    const tools = selected_tools.filter(s => s.mxi_inv_no_id !== row.mxi_inv_no_id);
    setSelectedTools(tools);
    if (tools.length <= 0) {
      setSelectTailDisabled(true);
      nextRemovalAtRef.current = 0; // reset cascade when empty
    }
  }

  const closeExtendedToolsDialog = (status: boolean) => {
    if (status === true) {
      //loan tools out but they will get marked as extended tools. 
      handlerToLoanTools(true, employeeUserId, null);
    }
    setExtendedToolsDialog(false);
    setSelectedTools([]);
    setSelectTailDisabled(true);
    // optional: clear all timers on full reset
    removalTimersRef.current.forEach(t => clearTimeout(t));
    removalTimersRef.current.clear();
    nextRemovalAtRef.current = 0;
  }

  const closeAreYouSureDialog = (status: boolean) => {
    if (status === true) {
      //loan tools out if user approves the "are you sure" dialog
      handlerToLoanTools(false, null, selectedTail);
      setSelectedTail("");
      setSelectTailDisabled(true);
    }
    setShowAreYouSureDialog(false);
    setSelectedTail("");
  }

  const onSelectTail = (tail: string) => {
    setSelectedTail(tail);
    setShowAreYouSureDialog(true);
  }

  const generateTableBody = (row: ToolData, index: number) => {
    return (
      <TableRow>
        <TableCell>{index}</TableCell>
        <TableCell>{row.description}</TableCell>
        <TableCell>{row.out_date_text}</TableCell>
        <TableCell>{row.days_allowed} days</TableCell>
      </TableRow>
    )
  }
  return (
    <div className="w-full">
      <br />
      {tabValue === 0 && <Select options={tails} selectedItem={selectedTail} widthPercentage width={10} onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => onSelectTail(e.target.value)} filter_by_text="Select Tail" disabled={selectTailDisabled} />}
      <Typography variant='h1' align='center' text={store.tools.mxi_tools.length === 0 ? 'Loading ...' : 'Please Scan your tool'} color="textPrimary"></Typography>
      <Typography variant='h5' align='center' text={"Press Esc to remove list"}></Typography>
      <Typography variant='h6' align='center' color='error' text={scanInput} />
      <input id="text-Box-Loan" value={scanInput} onChange={handleModifeRFID} className="sr-only"></input>
      <button id="press-Enter" onClick={() => handleEnterPressed()} className="sr-only"></button>
      <div>
        {
          selected_tools.map((data, index) => (
            <div className="flex flex-wrap mt-2 ml-[2%]">
              <Typography text={(index + 1).toString()} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 2, 0, 0]} />
              <Typography text={"Description : "} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 2, 0, 0]} />
              <Typography text={" " + data.description + " "} variant="h3" align="left" color="textPrimary" fontWeight={'normal'} margin={[0, 2, 0, 0]} />
              <div className="cursor-pointer" onClick={() => { }}><Typography text={"Days allowed : "} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 1, 0, 0]} /></div>
              <Typography text={data.days_allowed.toString()} variant="h3" align="left" color="textPrimary" fontWeight={'normal'} margin={[0, 5, 0, 0]} />
              <IconButton onClick={() => handleDeleteIconClick(data)}> <DeleteIcon className="text-red-600 cursor-pointer -mt-4" /></IconButton>
            </div>
          ))
        }
      </div>
      {snackbar_message && snackbar_message.length > 0 && <Snackbar onSnackBarClose={() => setSnackbarMessage("")} open={true} message={snackbar_message} severity='success' autoHide={true} horizontalPosition='right' />}
      <Dialog
        visible={extended_tools_dialog_visible}
        max_width={"md"} title={"Error !!  Tools to long in circulation"}
        context={
          <Table
            headers={["Name", "loaned out", "Allowed days"]}
            rows={extended_tools}
            generateBody={(row: ToolData, index: number) => generateTableBody(row, index)}
            defaultRowsPerPage={100}
          />
        }
        onClose={(status: boolean) => closeExtendedToolsDialog(status)}
        strict={true}
        show_options={true}
      />
      <Dialog
        visible={showAreYouSureDialog}
        max_width={"lg"}
        title={"Are you sure?"}
        context={
          <div>
            <Typography text={"You are now placing these tools onto " + selectedTail} align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 2, 0, 0]} />
            {selected_tools.map((data, index) => (
              <div className="flex flex-wrap mt-2 ml-[2%]">
                <Typography text={(index + 1).toString()} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 2, 0, 0]} />
                <Typography text={"Description : "} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 2, 0, 0]} />
                <Typography text={" " + data.description + " "} variant="h3" align="left" color="textPrimary" fontWeight={'normal'} margin={[0, 2, 0, 0]} />
                <div className="cursor-pointer" onClick={() => { }}><Typography text={"Days allowed : "} variant="h3" align="left" color="textPrimary" fontWeight={'bolder'} margin={[0, 1, 0, 0]} /></div>
                <Typography text={data.days_allowed.toString()} variant="h3" align="left" color="textPrimary" fontWeight={'normal'} margin={[0, 5, 0, 0]} />
              </div>
            ))}
          </div>
        }
        onClose={(status: boolean) => closeAreYouSureDialog(status)}
        strict={true}
        show_options={true}
      />
    </div>
  );
};
export default ToolRegisterList;
